import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Autocomplete, TextField, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import ShiftApiViewController from "../../redux/actions/shiftApiAction";
import EmployeeProfileVeiwController from "../../redux/actions/EmployeeProfileApiAction";
import FormDrawer from "../../components/layout/FormDrawer";
import { primaryButtonSx, inputSx } from "../../components/auth/authTheme";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";

import "../../styles/styles.css";
import "../../styles/layout.css";

const validationSchema = Yup.object({
  shift: Yup.string().required("Shift is required"),
  start_date: Yup.date().required("Start date is required"),
  employee_ids: Yup.array().min(1, "Select at least one employee").required("Employee is required"),
});

const validationSchemaEdit = Yup.object({
  shift: Yup.string().required("Shift is required"),
  start_date: Yup.date().required("Start date is required"),
});


function AddAssignShiftForm({ open, handleClose, editData = null }) {
  const dispatch = useDispatch();
  const { assignShift, updateAssignedShift, getShifts } = ShiftApiViewController();
  const { getEmployees } = EmployeeProfileVeiwController();
  const { loading, shiftsData } = useSelector((state) => state.shifts);
  const { employee_data } = useSelector((state) => state.employeeProfile);

  const isEdit = Boolean(editData?.id);

  const [initialValues, setInitialValues] = useState({ 
    employee_ids: [],
    shift: "",
    start_date: null,
    end_date: null,
  });

  useEffect(() => {
    if (open) {
      dispatch(getShifts());
      dispatch(getEmployees());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;
    setInitialValues({
      employee_ids: isEdit ? [editData?.employee] : [],
      shift: editData?.shift || "",
      start_date: editData?.start_date ? dayjs(editData.start_date) : null,
      end_date: editData?.end_date ? dayjs(editData.end_date) : null,
    });
  }, [open, editData, isEdit]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = {
        shift: values.shift,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
      };
      // If end_date is provided, use it. Otherwise, assign only for the start_date.
      payload.end_date = values.end_date ? values.end_date.format('YYYY-MM-DD') : payload.start_date;

      if (isEdit) {
        payload.employee = values.employee_ids[0];
        await dispatch(updateAssignedShift(editData.id, payload));
      } else {
        payload.employee_ids = values.employee_ids;
        await dispatch(assignShift(payload));
      }
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error submitting assigned shift form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={isEdit ? validationSchemaEdit : validationSchema}
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        submitForm,
        handleChange,
        handleBlur,
        setFieldValue
      }) => {
        const isSaving = isSubmitting || loading;

        const activeEmployees = (employee_data?.employees || []).filter(u => u.is_active);
        const activeShifts = (shiftsData || []).filter(s => s.is_active);

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={open}
              onClose={handleClose}
              title={isEdit ? "Edit Shift Assignment" : "Assign Shift"}
              subtitle={
                isEdit
                  ? "Update employee shift assignment"
                  : "Assign a shift to one or more employees"
              }
              footer={
                <>
                  <Button onClick={handleClose} color="inherit" disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    onClick={() => submitForm()}
                    sx={{
                      ...primaryButtonSx,
                      minWidth: 120,
                      "&.Mui-disabled": {
                        bgcolor: isSaving ? "#FF6A00" : undefined,
                        color: isSaving ? "#fff" : undefined,
                        opacity: isSaving ? 0.85 : undefined,
                      },
                    }}
                    startIcon={
                      isSaving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  >
                    {isSaving
                      ? isEdit
                        ? "Updating..."
                        : "Assigning..."
                      : isEdit
                        ? "Update"
                        : "Assign"}
                  </Button>
                </>
              }
            >
              
              <Autocomplete
                multiple={!isEdit}
                options={activeEmployees}
                getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.email || option.mobile_number})`}
                value={isEdit 
                  ? activeEmployees.find(e => e.id === values.employee_ids[0]) || null
                  : activeEmployees.filter(e => values.employee_ids.includes(e.id))}
                onChange={(event, newValue) => {
                  if (isEdit) {
                    setFieldValue("employee_ids", newValue ? [newValue.id] : []);
                  } else {
                    setFieldValue("employee_ids", newValue.map(v => v.id));
                  }
                }}
                disabled={isEdit}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Employee(s)"
                    error={touched.employee_ids && Boolean(errors.employee_ids)}
                    helperText={touched.employee_ids && errors.employee_ids}
                    sx={{ ...inputSx, mb: 3 }}
                    required
                  />
                )}
              />

              <Autocomplete
                options={activeShifts}
                getOptionLabel={(option) => option.shift_name}
                value={activeShifts.find(s => s.id === values.shift) || null}
                onChange={(event, newValue) => {
                  setFieldValue("shift", newValue ? newValue.id : "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Shift"
                    error={touched.shift && Boolean(errors.shift)}
                    helperText={touched.shift && errors.shift}
                    sx={{ ...inputSx, mb: 3 }}
                    required
                  />
                )}
              />

              <Box sx={{ display: 'flex', gap: 2, pb: 35 }}>
                <DatePicker
                  label="Start Date"
                  value={values.start_date}
                  onChange={(newValue) => setFieldValue("start_date", newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: touched.start_date && Boolean(errors.start_date),
                      helperText: touched.start_date && errors.start_date,
                      sx: { ...inputSx, mb: 3 }
                    }
                  }}
                />

                <DatePicker
                  label="End Date (Optional)"
                  value={values.end_date}
                  onChange={(newValue) => setFieldValue("end_date", newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: touched.end_date && Boolean(errors.end_date),
                      helperText: touched.end_date && errors.end_date,
                      sx: { ...inputSx, mb: 3 }
                    }
                  }}
                />
              </Box>

            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default AddAssignShiftForm;
